import axios from 'axios';

// Use relative URLs in production (when REACT_APP_BACKEND_URL is not set or matches current origin)
// This ensures the frontend always calls APIs on the same domain it's hosted on
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Configure axios defaults - credentials needed for session cookies
axios.defaults.withCredentials = true;

// API Service for KaiCheck
class ApiService {
  // Fact-Checking APIs
  async factCheck(claim, claimType = 'text', imageData = null, url = null, provider = 'openai', model = null) {
    try {
      // Use dedicated URL endpoint for video transcription
      if (claimType === 'url' && url) {
        const response = await axios.post(
          `${API}/url-check/analyze`,
          { url },
          { timeout: 180000 } // 3 minute timeout for video processing (longer videos need more time)
        );
        
        if (!response.data.success) {
          // Return user-friendly error from backend
          throw new Error(response.data.error || 'Failed to analyze video. Please try again.');
        }
        
        // Transform response to match standard fact-check format
        return {
          fact_check_id: response.data.fact_check_id,
          claim: response.data.transcript,
          veracity: response.data.veracity,
          confidence: response.data.confidence,
          summary: response.data.summary,
          sources: response.data.sources || [],
          category: response.data.category,
          claim_type: 'url',
          transcript_duration: response.data.duration,
          transcript_word_count: response.data.word_count
        };
      }
      
      const params = {};
      if (provider) params.provider = provider;
      if (model) params.model = model;

      const response = await axios.post(
        `${API}/fact-check/`,
        {
          claim,
          claim_type: claimType,
          image_data: imageData,
          url: url
        },
        { 
          params,
          timeout: 60000 // 60 second timeout for fact-checking (AI processing can take time)
        }
      );
      return response.data;
    } catch (error) {
      console.error('Fact check error:', error);
      
      // Handle URL/video specific errors
      if (claimType === 'url') {
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          throw new Error('⏳ Video processing timed out. This may happen with longer videos. Please try a shorter video (under 10 minutes) or paste the transcript in the Text tab.');
        }
        if (error.response?.status === 502 || error.response?.status === 504) {
          throw new Error('🔧 Server is busy processing videos. Please try again in a moment.');
        }
        // Pass through backend error message if available
        if (error.message && !error.message.includes('Network Error')) {
          throw error;
        }
        throw new Error('😕 Could not process this video. Please try again or paste the transcript in the Text tab.');
      }
      
      // Handle other errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Request timed out. Please try again with a smaller image or simpler claim.');
      }
      if (error.response?.status === 520) {
        throw new Error('Server error (520). The image may be too large. Please try a smaller image.');
      }
      throw error;
    }
  }

  async getFactCheckHistory(userId = null, limit = 50, skip = 0) {
    try {
      const params = { limit, skip };
      if (userId) params.user_id = userId;

      const response = await axios.get(`${API}/fact-check/history`, { params });
      return response.data;
    } catch (error) {
      console.error('Get history error:', error);
      throw error;
    }
  }

  async getFactCheck(factCheckId) {
    try {
      // Public endpoint - don't send credentials to avoid CORS issues
      const response = await axios.get(`${API}/fact-check/${factCheckId}`, {
        withCredentials: false
      });
      return response.data;
    } catch (error) {
      console.error('Get fact check error:', error);
      throw error;
    }
  }

  async getProviders() {
    try {
      const response = await axios.get(`${API}/fact-check/providers`);
      return response.data;
    } catch (error) {
      console.error('Get providers error:', error);
      throw error;
    }
  }

  // Authentication APIs
  async register(email, name, password, provider = 'email') {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        name,
        password,
        provider
      });
      return response.data;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  }

  async login(email, password) {
    try {
      const response = await axios.post(`${API}/auth/login`, null, {
        params: { email, password }
      });
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async processGoogleSession(sessionId) {
    try {
      const response = await axios.post(`${API}/auth/session`, null, {
        params: { session_id: sessionId }
      });
      return response.data;
    } catch (error) {
      console.error('Process session error:', error);
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      const response = await axios.get(`${API}/auth/me`);
      return response.data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  async logout() {
    try {
      const response = await axios.post(`${API}/auth/logout`);
      return response.data;
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await axios.get(`${API}/`);
      return response.data;
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  }

  // Payment APIs
  async createCheckoutSession(packageId) {
    try {
      // Get origin URL dynamically (never hardcode!)
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });
      return response.data;
    } catch (error) {
      console.error('Create checkout error:', error);
      throw error;
    }
  }

  async getPaymentStatus(sessionId) {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Get payment status error:', error);
      throw error;
    }
  }

  async getSubscriptionPackages() {
    try {
      const response = await axios.get(`${API}/payments/packages`);
      return response.data;
    } catch (error) {
      console.error('Get packages error:', error);
      throw error;
    }
  }

  // Google Fact Check Deep Dive - Available to ALL users
  async searchFactChecks(query) {
    try {
      const response = await axios.get(`${API}/deep-dive/search`, {
        params: { query }
      });
      return response.data;
    } catch (error) {
      console.error('Deep dive search error:', error);
      throw error;
    }
  }

  // Influencer Certification APIs
  async getInfluencerPricing() {
    try {
      const response = await axios.get(`${API}/influencer/pricing`);
      return response.data;
    } catch (error) {
      console.error('Get influencer pricing error:', error);
      throw error;
    }
  }

  async submitInfluencerApplication(application) {
    try {
      const response = await axios.post(`${API}/influencer/apply`, application);
      return response.data;
    } catch (error) {
      console.error('Submit influencer application error:', error);
      throw error;
    }
  }

  async getInfluencerApplicationStatus(applicationId) {
    try {
      const response = await axios.get(`${API}/influencer/application/${applicationId}`);
      return response.data;
    } catch (error) {
      console.error('Get application status error:', error);
      throw error;
    }
  }

  async verifyInfluencer(handle, platform = null) {
    try {
      const params = { handle };
      if (platform) params.platform = platform;
      const response = await axios.get(`${API}/influencer/verify/${handle}`, { params });
      return response.data;
    } catch (error) {
      console.error('Verify influencer error:', error);
      throw error;
    }
  }

  // Verified Directory APIs
  async getDirectoryCount() {
    try {
      const response = await axios.get(`${API}/directory/count`);
      return response.data;
    } catch (error) {
      console.error('Get directory count error:', error);
      throw error;
    }
  }

  async getDirectoryEntries(limit = 50, skip = 0) {
    try {
      const response = await axios.get(`${API}/directory/entries`, {
        params: { limit, skip }
      });
      return response.data;
    } catch (error) {
      console.error('Get directory entries error:', error);
      throw error;
    }
  }

  async getDirectoryEntry(entryId) {
    try {
      const response = await axios.get(`${API}/directory/entry/${entryId}`);
      return response.data;
    } catch (error) {
      console.error('Get directory entry error:', error);
      throw error;
    }
  }

  async getMyDirectoryStatus() {
    try {
      const response = await axios.get(`${API}/directory/my-status`);
      return response.data;
    } catch (error) {
      console.error('Get directory status error:', error);
      throw error;
    }
  }

  async optInToDirectory() {
    try {
      const response = await axios.post(`${API}/directory/opt-in`);
      return response.data;
    } catch (error) {
      console.error('Opt-in to directory error:', error);
      throw error;
    }
  }

  async optOutOfDirectory() {
    try {
      const response = await axios.post(`${API}/directory/opt-out`);
      return response.data;
    } catch (error) {
      console.error('Opt-out of directory error:', error);
      throw error;
    }
  }

  // Admin Directory APIs
  async addDirectoryEntry(entry) {
    try {
      const response = await axios.post(`${API}/directory/admin/add`, entry);
      return response.data;
    } catch (error) {
      console.error('Add directory entry error:', error);
      throw error;
    }
  }

  async removeDirectoryEntry(entryId) {
    try {
      const response = await axios.delete(`${API}/directory/admin/remove/${entryId}`);
      return response.data;
    } catch (error) {
      console.error('Remove directory entry error:', error);
      throw error;
    }
  }

  async updateDirectoryEntry(entryId, update) {
    try {
      const response = await axios.put(`${API}/directory/admin/update/${entryId}`, update);
      return response.data;
    } catch (error) {
      console.error('Update directory entry error:', error);
      throw error;
    }
  }

  async getAllDirectoryEntries(includeInactive = true) {
    try {
      const response = await axios.get(`${API}/directory/admin/all`, {
        params: { include_inactive: includeInactive }
      });
      return response.data;
    } catch (error) {
      console.error('Get all directory entries error:', error);
      throw error;
    }
  }

  // Delete Fact-Check (Admin or Premium user's own)
  async deleteFactCheck(factCheckId) {
    try {
      const response = await axios.delete(`${API}/fact-check/${factCheckId}`);
      return response.data;
    } catch (error) {
      console.error('Delete fact check error:', error);
      throw error;
    }
  }

  // User Profile APIs
  async getProfile() {
    try {
      const response = await axios.get(`${API}/profile/me`);
      return response.data;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  async updateProfile(data) {
    try {
      const response = await axios.put(`${API}/profile/me`, data);
      return response.data;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }

  async downloadMyData() {
    try {
      const response = await axios.get(`${API}/profile/download`);
      return response.data;
    } catch (error) {
      console.error('Download data error:', error);
      throw error;
    }
  }

  async deleteAccount() {
    try {
      const response = await axios.delete(`${API}/profile/me`);
      return response.data;
    } catch (error) {
      console.error('Delete account error:', error);
      throw error;
    }
  }

  // Generic request method for admin APIs
  async request(endpoint, options = {}) {
    try {
      const { method = 'GET', body = null, params = {} } = options;
      const config = {
        method,
        url: `${API}${endpoint}`,
        params,
        headers: {
          'Content-Type': 'application/json'
        }
      };
      
      if (body) {
        config.data = typeof body === 'string' ? body : JSON.stringify(body);
      }
      
      const response = await axios(config);
      return response.data;
    } catch (error) {
      console.error(`API request error (${endpoint}):`, error);
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  // Feature votes
  async voteForFeature(email, featureId, featureName) {
    try {
      const response = await axios.post(`${API}/feature-votes/vote`, {
        email,
        feature_id: featureId,
        feature_name: featureName
      });
      return response.data;
    } catch (error) {
      console.error('Feature vote error:', error);
      throw error;
    }
  }

  async getFeatureVoteCount(featureId) {
    try {
      const response = await axios.get(`${API}/feature-votes/count/${featureId}`);
      return response.data;
    } catch (error) {
      console.error('Get vote count error:', error);
      return { vote_count: 0 };
    }
  }

  // Password reset
  async requestPasswordReset(email) {
    try {
      const response = await axios.post(`${API}/password/request-reset`, { email });
      return response.data;
    } catch (error) {
      console.error('Password reset request error:', error);
      throw error;
    }
  }

  async resetPassword(token, newPassword) {
    try {
      const response = await axios.post(`${API}/password/reset`, {
        token,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  async validateResetToken(token) {
    try {
      const response = await axios.get(`${API}/password/validate-token/${token}`);
      return response.data;
    } catch (error) {
      console.error('Token validation error:', error);
      return { valid: false };
    }
  }

  // Stats
  async getClaimsVerifiedCount() {
    try {
      const response = await axios.get(`${API}/stats/claims-count`);
      return response.data;
    } catch (error) {
      console.error('Get claims count error:', error);
      return { count: 0 };
    }
  }
}

const apiService = new ApiService();
export default apiService;